// Shared color-derivation math, extracted from
// src/components/landing/themeSections.js so it has exactly one
// implementation instead of being reimplemented per consumer. Originally
// built to turn a landing-page theme preset (background/accent/text/muted)
// into a full set of derived surfaces and contrast-safe text colors;
// src/theme/dashboardTheme.js reuses it the same way to derive the
// dashboard's light theme from the `archival` preset.

// Mixes two hex colors by `amount` (0 = hex, 1 = other). Used to derive
// raised/panel/panelHi surfaces from a theme's own two poles: a light theme
// lifts toward its ink (dark text), a dark theme lifts toward its light text.
export const mix = (hex, other, amount) => {
  const p = (h) => {
    const s = h.replace("#", "");
    return [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16));
  };
  const [r1, g1, b1] = p(hex);
  const [r2, g2, b2] = p(other);
  const c = (a, b) => Math.round(a + (b - a) * amount).toString(16).padStart(2, "0");
  return `#${c(r1, r2)}${c(g1, g2)}${c(b1, b2)}`;
};

// "R,G,B" channel string, for building rgba(...) values from a hex color in
// CSS custom properties (e.g. --t-accent-rgb, --color-volt-rgb).
export const rgbChannels = (hex) => {
  const s = hex.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16)).join(",");
};

// Relative luminance (WCAG definition), so contrast is measured rather than
// assumed.
export const relLum = (hex) => {
  const s = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16) / 255);
  const f = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

// Whether a color classifies as a "light" theme background, by luminance
// rather than by hand-labelling each preset — so a new preset works
// automatically without touching any derivation logic.
export const isLight = (hex) => relLum(hex) > 0.4;

// WCAG contrast ratio between two colors.
export const contrast = (a, b) => {
  const [x, y] = [relLum(a), relLum(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
};

/**
 * A version of `accent` that is legible as TEXT on `surface`.
 *
 * Real accent colors are often chosen to sit on video/fills, where they
 * don't need to clear body-text contrast. Several fall under 4.5:1 when used
 * as text on their own theme's surfaces — e.g. the landing page's archival
 * preset's burnt orange is 3.49:1 on its own cream panel, investigation's
 * alarm red is 3.90:1 on its raised panel. Those hexes are the product's and
 * must not be repainted, so instead the accent is walked toward `text` until
 * it clears the floor, and only that walked value is used for accent TEXT.
 * Fills, dots, bars and hairlines keep the true accent, where the
 * contrast-as-text ratio doesn't apply.
 */
export const readableAccent = (accent, surface, text) => {
  if (contrast(accent, surface) >= 4.5) return accent;
  for (let i = 1; i <= 20; i += 1) {
    const candidate = mix(accent, text, i * 0.05);
    if (contrast(candidate, surface) >= 4.5) return candidate;
  }
  return text;
};
